import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface DiagnoseResult {
  minDate: string | null;
  maxDate: string | null;
  totalCount: number;
  sampleDates: string[];
}

export default function GameDateDiagnose() {
  const [result, setResult] = useState<DiagnoseResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [queryTest, setQueryTest] = useState<any>(null);

  useEffect(() => {
    runDiagnose();
  }, []);

  async function runDiagnose() {
    try {
      setLoading(true);
      
      // 1. 查询最早日期
      const { data: minData } = await supabase
        .from('game_records')
        .select('game_date')
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .order('game_date', { ascending: true })
        .limit(1);

      // 2. 查询最晚日期
      const { data: maxData } = await supabase
        .from('game_records')
        .select('game_date')
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .order('game_date', { ascending: false })
        .limit(1);

      // 3. 查询总数
      const { count: totalCount } = await supabase
        .from('game_records')
        .select('*', { count: 'exact', head: true })
        .eq('is_deleted', 'n')
        .eq('status', 'valid');

      // 4. 随机采样 20 个日期
      const { data: sampleData } = await supabase
        .from('game_records')
        .select('game_date, game_number')
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .order('game_date', { ascending: true })
        .order('game_number', { ascending: true })
        .limit(20);

      // 5. 测试日期范围查询 - 使用纯日期字符串
      const testStartDate = '2020-01-01';
      const testEndDate = '2030-12-31';
      
      const { data: rangeTestData } = await supabase
        .from('game_records')
        .select('game_date')
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .gte('game_date', testStartDate)
        .lte('game_date', testEndDate);

      // 6. 测试早期日期查询（2020 年）
      const { data: earlyYearData } = await supabase
        .from('game_records')
        .select('game_date, game_number')
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .gte('game_date', '2020-01-01')
        .lte('game_date', '2020-12-31')
        .order('game_date', { ascending: true });

      // 7. 测试 ISO 时间戳查询（有问题的方式）
      const isoStart = new Date(2020, 0, 1).toISOString(); // "2020-01-01T00:00:00.000Z"
      const isoEnd = new Date(2020, 11, 31, 23, 59, 59).toISOString();
      
      const { data: isoTestData } = await supabase
        .from('game_records')
        .select('game_date')
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .gte('game_date', isoStart)
        .lte('game_date', isoEnd);

      setResult({
        minDate: minData && minData.length > 0 ? minData[0].game_date : null,
        maxDate: maxData && maxData.length > 0 ? maxData[0].game_date : null,
        totalCount: totalCount || 0,
        sampleDates: sampleData?.map(d => `${d.game_date} (#${d.game_number})`) || [],
      });

      setQueryTest({
        rangeTest: {
          startDate: testStartDate,
          endDate: testEndDate,
          count: rangeTestData?.length || 0,
        },
        earlyYearTest: {
          year: 2020,
          count: earlyYearData?.length || 0,
          sample: earlyYearData?.slice(0, 5).map(d => `${d.game_date} (#${d.game_number})`) || [],
        },
        isoTimestampTest: {
          isoStart,
          isoEnd,
          count: isoTestData?.length || 0,
        },
      });

      setLoading(false);
    } catch (err: any) {
      setError(err.message || String(err));
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-purple-900 mb-6">
          📊 游戏日期诊断工具
        </h1>

        {loading && (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">正在查询数据库...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-red-800 mb-2">❌ 查询失败</h3>
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {result && !loading && (
          <div className="space-y-6">
            {/* 基础统计 */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-purple-800 mb-4">📈 基础统计</h2>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-sm text-purple-600">最早记录</p>
                  <p className="text-2xl font-bold text-purple-900">{result.minDate || '无数据'}</p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-4">
                  <p className="text-sm text-indigo-600">最晚记录</p>
                  <p className="text-2xl font-bold text-indigo-900">{result.maxDate || '无数据'}</p>
                </div>
                <div className="bg-pink-50 rounded-lg p-4">
                  <p className="text-sm text-pink-600">总记录数</p>
                  <p className="text-2xl font-bold text-pink-900">{result.totalCount}</p>
                </div>
              </div>
            </div>

            {/* 采样数据 */}
            {result.sampleDates.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-purple-800 mb-4">📋 前 20 条记录采样</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {result.sampleDates.map((date, idx) => (
                    <div key={idx} className="bg-gray-50 rounded px-3 py-2 text-sm text-gray-700">
                      {date}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 查询测试结果 */}
            {queryTest && (
              <>
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h2 className="text-xl font-bold text-green-800 mb-4">✅ 日期范围查询测试（正确方式）</h2>
                  <div className="space-y-3">
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-sm text-green-700">查询范围</p>
                      <p className="font-mono text-green-900">
                        {queryTest.rangeTest.startDate} ~ {queryTest.rangeTest.endDate}
                      </p>
                      <p className="text-3xl font-bold text-green-800 mt-2">
                        返回 {queryTest.rangeTest.count} 条记录
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h2 className="text-xl font-bold text-blue-800 mb-4">🔍 2020 年全年查询测试</h2>
                  <div className="space-y-3">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-sm text-blue-700">查询条件</p>
                      <p className="font-mono text-blue-900">
                        game_date BETWEEN '2020-01-01' AND '2020-12-31'
                      </p>
                      <p className="text-3xl font-bold text-blue-800 mt-2">
                        返回 {queryTest.earlyYearTest.count} 条记录
                      </p>
                      {queryTest.earlyYearTest.sample.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-blue-200">
                          <p className="text-xs text-blue-600 mb-2">样本数据：</p>
                          <div className="flex flex-wrap gap-2">
                            {queryTest.earlyYearTest.sample.map((s: string, idx: number) => (
                              <span key={idx} className="bg-white px-2 py-1 rounded text-xs text-blue-800">
                                {s}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h2 className="text-xl font-bold text-red-800 mb-4">⚠️ ISO 时间戳查询测试（问题方式）</h2>
                  <div className="space-y-3">
                    <div className="bg-red-50 rounded-lg p-4">
                      <p className="text-sm text-red-700">查询条件（有问题的方式）</p>
                      <p className="font-mono text-xs text-red-900 break-all">
                        {queryTest.isoTimestampTest.isoStart} ~ {queryTest.isoTimestampTest.isoEnd}
                      </p>
                      <p className="text-3xl font-bold text-red-800 mt-2">
                        返回 {queryTest.isoTimestampTest.count} 条记录
                      </p>
                      {queryTest.isoTimestampTest.count === 0 && queryTest.earlyYearTest.count > 0 && (
                        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-300 rounded">
                          <p className="text-sm text-yellow-800 font-semibold">
                            ⚠️ 发现问题：ISO 时间戳查询返回 0 条，但日期字符串查询有数据！
                          </p>
                          <p className="text-xs text-yellow-700 mt-1">
                            这证明时区转换导致了查询失败。
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* 刷新按钮 */}
            <button
              onClick={runDiagnose}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold py-4 px-6 rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg"
            >
              🔄 重新查询
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
